import { getStoredAttribution } from './attribution';
import { getGaClientId } from './gaClientId';
import { sendLifecycleEvent } from '../api/lifecycleEventsApi';

const TRACKED_ONCE_PREFIX = 'tendera:tracked-event:';

const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
const LIFECYCLE_EVENTS = new Set([
    'signup_completed',
    'onboarding_started',
    'onboarding_completed',
    'first_results_shown',
    'no_results_shown',
    'login_completed',
    'pricing_viewed',
    'pro_lock_clicked',
    'checkout_started',
    'profile_results_shown',
]);

const normalizeValue = (value) => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    return undefined;
};

const sanitizeProperties = (properties = {}) => Object.entries(properties).reduce((acc, [key, value]) => {
    const normalized = normalizeValue(value);
    if (normalized !== undefined) {
        acc[key] = normalized;
    }
    return acc;
}, {});

const pickAttributionValue = (firstTouch, latestTouch, key) => latestTouch?.[key] || firstTouch?.[key] || undefined;

const getAttributionContext = () => {
    const { first_touch: firstTouch, latest_touch: latestTouch } = getStoredAttribution();

    return sanitizeProperties({
        utm_id: pickAttributionValue(firstTouch, latestTouch, 'utm_id'),
        source: pickAttributionValue(firstTouch, latestTouch, 'utm_source'),
        medium: pickAttributionValue(firstTouch, latestTouch, 'utm_medium'),
        campaign: pickAttributionValue(firstTouch, latestTouch, 'utm_campaign'),
        content: pickAttributionValue(firstTouch, latestTouch, 'utm_content'),
        term: pickAttributionValue(firstTouch, latestTouch, 'utm_term'),
        creative_format: pickAttributionValue(firstTouch, latestTouch, 'utm_creative_format'),
        marketing_tactic: pickAttributionValue(firstTouch, latestTouch, 'utm_marketing_tactic'),
        gclid: pickAttributionValue(firstTouch, latestTouch, 'gclid'),
        gbraid: pickAttributionValue(firstTouch, latestTouch, 'gbraid'),
        wbraid: pickAttributionValue(firstTouch, latestTouch, 'wbraid'),
        fbclid: pickAttributionValue(firstTouch, latestTouch, 'fbclid'),
        msclkid: pickAttributionValue(firstTouch, latestTouch, 'msclkid'),
        li_fat_id: pickAttributionValue(firstTouch, latestTouch, 'li_fat_id'),
        ttclid: pickAttributionValue(firstTouch, latestTouch, 'ttclid'),
        twclid: pickAttributionValue(firstTouch, latestTouch, 'twclid'),
        landing_page: pickAttributionValue(firstTouch, latestTouch, 'landing_page'),
        source_page: pickAttributionValue(firstTouch, latestTouch, 'source_page'),
        source_path: pickAttributionValue(firstTouch, latestTouch, 'source_path'),
        source_url: pickAttributionValue(firstTouch, latestTouch, 'source_url'),
        source_referrer: pickAttributionValue(firstTouch, latestTouch, 'source_referrer'),
        page_referrer: pickAttributionValue(firstTouch, latestTouch, 'page_referrer'),
        source_page_title: pickAttributionValue(firstTouch, latestTouch, 'page_title'),
        source_page_type: pickAttributionValue(firstTouch, latestTouch, 'page_type'),
        cta_id: pickAttributionValue(firstTouch, latestTouch, 'cta_id'),
        cta_location: pickAttributionValue(firstTouch, latestTouch, 'cta_location'),
        cta_label: pickAttributionValue(firstTouch, latestTouch, 'cta_label'),
        destination_url: pickAttributionValue(firstTouch, latestTouch, 'destination_url'),
        intent: pickAttributionValue(firstTouch, latestTouch, 'intent'),
        plan: pickAttributionValue(firstTouch, latestTouch, 'plan'),
        target_plan: pickAttributionValue(firstTouch, latestTouch, 'target_plan'),
        campaign_id: pickAttributionValue(firstTouch, latestTouch, 'campaign_id'),
        campaign_name: pickAttributionValue(firstTouch, latestTouch, 'campaign_name'),
        adgroup_id: pickAttributionValue(firstTouch, latestTouch, 'adgroup_id'),
        adgroup_name: pickAttributionValue(firstTouch, latestTouch, 'adgroup_name'),
        ad_id: pickAttributionValue(firstTouch, latestTouch, 'ad_id'),
        ad_name: pickAttributionValue(firstTouch, latestTouch, 'ad_name'),
        creative_id: pickAttributionValue(firstTouch, latestTouch, 'creative_id'),
        creative_name: pickAttributionValue(firstTouch, latestTouch, 'creative_name'),
        placement: pickAttributionValue(firstTouch, latestTouch, 'placement'),
        keyword: pickAttributionValue(firstTouch, latestTouch, 'keyword'),
        matchtype: pickAttributionValue(firstTouch, latestTouch, 'matchtype'),
        network: pickAttributionValue(firstTouch, latestTouch, 'network'),
        app_entry_path: pickAttributionValue(firstTouch, latestTouch, 'app_entry_path'),
        app_entry_url: pickAttributionValue(firstTouch, latestTouch, 'app_entry_url'),
        first_source: firstTouch?.utm_source,
        first_medium: firstTouch?.utm_medium,
        first_campaign: firstTouch?.utm_campaign,
        first_content: firstTouch?.utm_content,
        first_term: firstTouch?.utm_term,
        first_landing_page: firstTouch?.landing_page,
        first_source_page: firstTouch?.source_page,
        first_cta_id: firstTouch?.cta_id,
        first_cta_location: firstTouch?.cta_location,
        latest_source: latestTouch?.utm_source,
        latest_medium: latestTouch?.utm_medium,
        latest_campaign: latestTouch?.utm_campaign,
        latest_content: latestTouch?.utm_content,
        latest_term: latestTouch?.utm_term,
        latest_landing_page: latestTouch?.landing_page,
        latest_source_page: latestTouch?.source_page,
        latest_cta_id: latestTouch?.cta_id,
        latest_cta_location: latestTouch?.cta_location,
        attribution_first_touch_at: firstTouch?.captured_at,
        attribution_latest_touch_at: latestTouch?.captured_at,
        attribution_landing_path: latestTouch?.landing_path || firstTouch?.landing_path
    });
};

const getPageContext = () => {
    if (typeof window === 'undefined') return {};

    return sanitizeProperties({
        app_path: window.location.pathname,
        page_title: window.document?.title
    });
};

// Stored touch -> the backend's per-organization attribution shape.
const toBackendTouch = (touch) => (touch?.captured_at ? {
    capturedAt: touch.captured_at,
    source: touch.utm_source,
    medium: touch.utm_medium,
    campaign: touch.utm_campaign,
    content: touch.utm_content,
    term: touch.utm_term,
    gclid: touch.gclid,
    referrer: touch.source_referrer || touch.page_referrer,
    landingPage: touch.landing_page || touch.app_entry_url,
} : undefined);

const sendLifecycleEventToBff = (eventName, payload) => {
    if (!LIFECYCLE_EVENTS.has(eventName)) {
        return;
    }

    const { first_touch: firstTouch, latest_touch: latestTouch } = getStoredAttribution();

    sendLifecycleEvent({
        event: eventName,
        occurredAt: payload.event_time,
        source: payload.first_source || payload.source,
        medium: payload.first_medium || payload.medium,
        campaign: payload.first_campaign || payload.campaign,
        landingPage: payload.first_landing_page || payload.landing_page,
        ctaId: payload.first_cta_id || payload.cta_id,
        firstTouch: toBackendTouch(firstTouch),
        latestTouch: toBackendTouch(latestTouch),
        gaClientId: getGaClientId(),
        resultsCount: typeof payload.results_count === 'number' ? payload.results_count : undefined,
    }).catch(error => {
        console.warn('Failed to send lifecycle event', eventName, error);
    });
};

export const trackAppEvent = (eventName, properties = {}, context = {}) => {
    if (!EVENT_NAME_PATTERN.test(eventName)) {
        return null;
    }

    const payload = {
        event: eventName,
        event_source: 'app',
        app_event: true,
        app_environment: import.meta.env.MODE,
        event_time: new Date().toISOString(),
        ...getAttributionContext(),
        ...getPageContext(),
        ...sanitizeProperties(context),
        ...sanitizeProperties(properties)
    };

    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        window.dispatchEvent(new CustomEvent('tendera:app_event', { detail: payload }));
    }

    sendLifecycleEventToBff(eventName, payload);

    return payload;
};

export const trackAppEventOnce = (key, eventName, properties = {}, context = {}) => {
    if (typeof window === 'undefined') {
        return trackAppEvent(eventName, properties, context);
    }

    const storageKey = `${TRACKED_ONCE_PREFIX}${key}`;
    try {
        if (window.sessionStorage.getItem(storageKey)) return null;
        window.sessionStorage.setItem(storageKey, 'true');
    } catch {
        // Continue and track once best-effort when sessionStorage is unavailable.
    }

    return trackAppEvent(eventName, properties, context);
};
